/**
 * =====================================================================================
 * ÍNDICE DE SERVICIOS - index.js
 * =====================================================================================
 *
 * Este archivo actúa como punto de entrada central para todos los servicios.
 * Es un patrón común llamado "barrel export" o "re-exportación".
 *
 * ¿QUÉ ES UN BARREL EXPORT?
 * -------------------------
 * En lugar de tener que importar cada servicio desde su archivo individual:
 *
 * // SIN barrel (múltiples imports):
 * import authService from '@/services/auth.service'
 * import catalogosService from '@/services/catalogos.service'
 * import facturasService from '@/services/facturas.service'
 * import emisoresService from '@/services/emisores.service'
 *
 * Podemos importar todo desde un solo lugar:
 *
 * // CON barrel (un solo import):
 * import { authService, catalogosService, facturasService } from '@/services'
 *
 * // O importar el objeto default:
 * import services from '@/services'
 * services.auth.login(...)
 * services.catalogos.getDepartamentos()
 *
 * BENEFICIOS:
 * -----------
 * 1. CÓDIGO MÁS LIMPIO: Menos líneas de import
 * 2. ORGANIZACIÓN: Un solo punto de entrada para servicios
 * 3. REFACTORING: Si renombramos un archivo, solo cambiamos aquí
 * 4. AUTOCOMPLETADO: IDEs sugieren todos los servicios disponibles
 *
 * ESTRUCTURA DEL DIRECTORIO:
 * --------------------------
 * src/services/
 * ├── index.js                    ← Este archivo (barrel)
 * ├── api.js                      ← Cliente HTTP Axios
 * ├── auth.service.js             ← Autenticación
 * ├── catalogos.service.js        ← Catálogos MH
 * ├── emisores.service.js         ← Gestión de emisores
 * ├── receptores.service.js       ← Gestión de clientes
 * ├── productosServicios.service.js ← Catálogo de productos
 * ├── facturas.service.js         ← Emisión de facturas
 * └── sucursales.service.js       ← Sucursales/establecimientos
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * Este archivo es importado por:
 * - src/store/modules/*.js → Actions de Vuex que llaman a servicios
 * - src/views/*.vue → Componentes que necesitan múltiples servicios
 * - src/components/*.vue → Componentes que usan servicios
 *
 * =====================================================================================
 */

// =====================================================================================
// IMPORTACIONES DE SERVICIOS INDIVIDUALES
// =====================================================================================
//
// Importamos cada servicio desde su archivo correspondiente.
// Usamos rutas relativas (./) porque estamos en el mismo directorio.

/**
 * api: Instancia de Axios configurada con interceptores
 * @see ./api.js
 */
import api from './api'

/**
 * authService: Login, logout, cambio de contraseña
 * @see ./auth.service.js
 */
import authService from './auth.service'

/**
 * catalogosService: Catálogos del Ministerio de Hacienda
 * @see ./catalogos.service.js
 */
import catalogosService from './catalogos.service'

/**
 * emisoresService: CRUD de empresas emisoras
 * @see ./emisores.service.js
 */
import emisoresService from './emisores.service'

/**
 * receptoresService: CRUD de clientes/receptores
 * @see ./receptores.service.js
 */
import receptoresService from './receptores.service'

/**
 * productosServiciosService: Catálogo de productos y servicios
 * @see ./productosServicios.service.js
 */
import productosServiciosService from './productosServicios.service'

/**
 * facturasService: Creación y gestión de facturas electrónicas
 * @see ./facturas.service.js
 */
import facturasService from './facturas.service'

/**
 * sucursalesService: Gestión de sucursales/establecimientos
 * @see ./sucursales.service.js
 */
import sucursalesService from './sucursales.service'

/**
 * dashboardService: KPIs, estadísticas y analíticas
 * @see ./dashboard.service.js
 */
import dashboardService from './dashboard.service'

/**
 * exportService: Exportación de datos a Excel
 * @see ./export.service.js
 */
import exportService from './export.service'

import suscripcionesService from './suscripciones.service'

// =====================================================================================
// EXPORTACIONES NOMBRADAS (Named Exports)
// =====================================================================================
//
// Permiten importar servicios específicos usando desestructuración:
// import { authService, facturasService } from '@/services'
//
// Es la forma recomendada cuando solo necesitas algunos servicios.

export {
  api,
  authService,
  catalogosService,
  emisoresService,
  receptoresService,
  productosServiciosService,
  facturasService,
  sucursalesService,
  dashboardService,
  exportService,
  suscripcionesService
}

// =====================================================================================
// EXPORTACIÓN DEFAULT (Objeto con todos los servicios)
// =====================================================================================
//
// Permite importar todos los servicios como un objeto:
// import services from '@/services'
// services.auth.login(...)
// services.facturas.create(...)
//
// Útil cuando necesitas acceso a muchos servicios o prefieres
// un namespace claro para evitar conflictos de nombres.
//
// NOTA: Usamos nombres cortos (auth, catalogos) en lugar de
// los nombres largos (authService, catalogosService) para
// una sintaxis más limpia: services.auth vs services.authService

export default {
  /**
   * Cliente HTTP Axios configurado
   * Raramente se usa directamente; los servicios ya lo encapsulan
   */
  api,

  /**
   * Servicio de autenticación
   * @example services.auth.login(email, password)
   */
  auth: authService,

  /**
   * Catálogos del Ministerio de Hacienda
   * @example services.catalogos.getDepartamentos()
   */
  catalogos: catalogosService,

  /**
   * Gestión de empresas emisoras
   * @example services.emisores.getById(1)
   */
  emisores: emisoresService,

  /**
   * Gestión de clientes/receptores
   * @example services.receptores.search("empresa")
   */
  receptores: receptoresService,

  /**
   * Catálogo de productos y servicios
   * @example services.productosServicios.search("laptop")
   */
  productosServicios: productosServiciosService,

  /**
   * Creación y gestión de facturas
   * @example services.facturas.create(documento, sucursalId)
   */
  facturas: facturasService,

  /**
   * Gestión de sucursales/establecimientos
   * @example services.sucursales.getAll()
   */
  sucursales: sucursalesService,

  /**
   * KPIs, estadísticas y analíticas del dashboard
   * @example services.dashboard.getKPIs()
   */
  dashboard: dashboardService,

  /**
   * Exportación de datos a Excel
   * @example services.export.exportarFacturas()
   */
  export: exportService,

  suscripciones: suscripcionesService
}

/**
 * =====================================================================================
 * EJEMPLOS DE USO
 * =====================================================================================
 *
 * OPCIÓN 1 - Named exports (recomendado para imports selectivos):
 * ---------------------------------------------------------------
 * import { authService, facturasService } from '@/services'
 *
 * // Usar directamente
 * await authService.login(email, password)
 * await facturasService.create(documento, sucursalId)
 *
 *
 * OPCIÓN 2 - Default export (para acceso a todos los servicios):
 * --------------------------------------------------------------
 * import services from '@/services'
 *
 * // Acceder con namespace
 * await services.auth.login(email, password)
 * await services.facturas.create(documento, sucursalId)
 * const deptos = await services.catalogos.getDepartamentos()
 *
 *
 * OPCIÓN 3 - Mixto (default + named):
 * -----------------------------------
 * import services, { api } from '@/services'
 *
 * // api para peticiones custom, services para el resto
 * await api.get('/custom-endpoint')
 * await services.auth.login(email, password)
 *
 * =====================================================================================
 */
